import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
  Chip,
  Rating,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  Badge,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  WhatsApp,
  Phone,
  Email,
  LocationOn,
  Schedule,
  Store,
  ChevronLeft,
  ChevronRight,
  Star,
  ShoppingCart
} from '@mui/icons-material';
import boutiqueService from '../services/boutiqueService';
import { productService } from '../services/productService';
import reviewService from '../services/reviewService';
import { useAuth } from '../context/AuthContext';
import ImageCard from '../components/ImageCard';

const BoutiqueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [boutique, setBoutique] = useState(null);
  const [boutiques, setBoutiques] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewOpen, setEditReviewOpen] = useState(false);
  const [editReviewRating, setEditReviewRating] = useState(0);
  const [editReviewComment, setEditReviewComment] = useState('');
  const [productImageIndices, setProductImageIndices] = useState({});

  useEffect(() => {
    const fetchBoutiqueAndData = async () => {
      try {
        setLoading(true);
        // Récupérer la boutique actuelle
        const boutiqueData = await boutiqueService.getBoutique(id);
        setBoutique(boutiqueData);
        
        // Récupérer la liste des boutiques pour la navigation
        const boutiquesData = await boutiqueService.getBoutiques();
        setBoutiques(boutiquesData.boutiques || boutiquesData);
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement de la boutique:', err);
        setError('Boutique non trouvée');
      } finally {
        setLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        // Récupérer les produits de cette boutique
        const productsData = await boutiqueService.getBoutiqueProducts(id);
        setProducts(productsData.products || []);
      } catch (err) {
        console.error('Erreur lors du chargement des produits:', err);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const reviewsData = await reviewService.getBoutiqueReviews(id);
        setReviews(reviewsData.reviews || []);
      } catch (err) {
        console.error('Erreur lors du chargement des avis:', err);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    if (id) {
      fetchBoutiqueAndData();
      fetchProducts();
      fetchReviews();
    }
  }, [id]);

  // Gérer la soumission d'un avis
  const handleSubmitReview = async () => {
    if (!reviewRating || reviewRating < 1) {
      alert('Veuillez sélectionner une note');
      return;
    }

    try {
      await reviewService.createBoutiqueReview(id, {
        rating: reviewRating,
        comment: reviewComment
      });
      
      // Recharger les avis et les données de la boutique
      const reviewsData = await reviewService.getBoutiqueReviews(id);
      setReviews(reviewsData.reviews || []);
      
      const boutiqueData = await boutiqueService.getBoutique(id);
      setBoutique(boutiqueData);
      
      // Réinitialiser le formulaire
      setReviewRating(0);
      setReviewComment('');
      setReviewFormOpen(false);
    } catch (err) {
      console.error('Erreur lors de la soumission de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de la soumission de l\'avis');
    }
  };

  // Gérer l'édition d'un avis
  const handleEditReview = (review) => {
    setEditingReview(review);
    setEditReviewRating(review.rating);
    setEditReviewComment(review.comment || '');
    setEditReviewOpen(true);
  };

  // Gérer la modification d'un avis
  const handleUpdateReview = async () => {
    if (!editReviewRating || editReviewRating < 1) {
      alert('Veuillez sélectionner une note');
      return;
    }

    try {
      await reviewService.updateReview(editingReview.id, {
        rating: editReviewRating,
        comment: editReviewComment
      });
      
      // Recharger les avis et les données de la boutique
      const reviewsData = await reviewService.getBoutiqueReviews(id);
      setReviews(reviewsData.reviews || []);
      
      const boutiqueData = await boutiqueService.getBoutique(id);
      setBoutique(boutiqueData);
      
      // Réinitialiser le formulaire
      setEditingReview(null);
      setEditReviewRating(0);
      setEditReviewComment('');
      setEditReviewOpen(false);
      
    } catch (err) {
      console.error('Erreur lors de la modification de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de la modification de l\'avis');
    }
  };

  // Gérer la suppression d'un avis
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre commentaire ?')) {
      return;
    }

    try {
      await reviewService.deleteReview(reviewId);
      
      // Recharger les avis et les données de la boutique
      const reviewsData = await reviewService.getBoutiqueReviews(id);
      setReviews(reviewsData.reviews || []);
      
      const boutiqueData = await boutiqueService.getBoutique(id);
      setBoutique(boutiqueData);
      
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de la suppression de l\'avis');
    }
  };

  // Vérifier si l'utilisateur a déjà laissé un avis
  const hasUserReviewed = useMemo(() => {
    if (!isAuthenticated || !user || !reviews.length) {
      return false;
    }
    return reviews.some(review => review.reviewer?.id === user.id);
  }, [isAuthenticated, user, reviews]);

  // Navigation entre boutiques
  const getCurrentBoutiqueIndex = () => {
    return boutiques.findIndex(b => b.id === id);
  };

  const getPreviousBoutique = () => {
    const currentIndex = getCurrentBoutiqueIndex();
    if (currentIndex > 0) {
      return boutiques[currentIndex - 1];
    }
    return null;
  };

  const getNextBoutique = () => {
    const currentIndex = getCurrentBoutiqueIndex();
    if (currentIndex < boutiques.length - 1) {
      return boutiques[currentIndex + 1];
    }
    return null;
  };

  // Fonctions pour naviguer entre les images dans les cartes
  const handleProductImageNext = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const handleProductImagePrev = (e, productId, totalImages) => {
    e.stopPropagation();
    setProductImageIndices(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handlePreviousBoutique = () => {
    const previousBoutique = getPreviousBoutique();
    if (previousBoutique) {
      navigate(`/boutiques/${previousBoutique.id}`);
    }
  };

  const handleNextBoutique = () => {
    const nextBoutique = getNextBoutique();
    if (nextBoutique) {
      navigate(`/boutiques/${nextBoutique.id}`);
    }
  };

  const handleWhatsAppClick = () => {
    // Si la boutique existe, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
    const contactPhone = boutique 
      ? (boutique.whatsapp || boutique.phone)
      : boutique?.user?.phone;
    
    if (contactPhone) {
      const phoneNumber = contactPhone;
      const message = `Bonjour, je suis intéressé par les produits${boutique?.name ? ` de ${boutique.name}` : ''}`;
      window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs Skeleton */}
        <Skeleton variant="text" width={300} height={24} sx={{ mb: 3 }} />
        
        {/* Navigation Skeleton */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={100} height={36} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="text" width={60} height={20} />
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Informations boutique Skeleton */}
          <Grid item xs={12} md={4.2}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                {/* Logo et nom */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={60} height={60} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={32} />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Skeleton variant="text" width={100} height={20} />
                        <Skeleton variant="text" width={60} height={20} />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Description */}
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={100} height={24} />
                  <Skeleton variant="rectangular" width="100%" height={150} sx={{ mt: 1, borderRadius: 1 }} />
                </Box>

                {/* Spécialités */}
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={100} height={24} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', }}>
                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1, }} />
                    <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1, }} />
                    <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, }} />
                  </Box>
                </Box>

                {/* Contact */}
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={100} height={24} />
                  <Box sx={{ mt: 1 }}>
                    {[...Array(4)].map((_, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width="70%" height={20} />
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Skeleton variant="rectangular" width="100%" height={40} />
                  <Skeleton variant="rectangular" width={120} height={40} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Produits Skeleton */}
          <Grid item xs={12} md={7.8}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1.5,
                  justifyContent: { xs: 'space-between', sm: 'flex-start' }
                }}>
                  {[...Array(6)].map((_, index) => (
                    <Card key={index} sx={{ 
                      width: { xs: 'calc((100% - 12px) / 2)', sm: 160 },
                      height: 190
                    }}>
                      <Skeleton variant="rectangular" width="100%" height={100} />
                      <CardContent sx={{ p: 1 }}>
                        <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.25 }} />
                        <Skeleton variant="text" width="50%" height={14} />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Avis Section Skeleton */}
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="rectangular" width={140} height={36} />
            </Box>
            {[...Array(2)].map((_, index) => (
              <Box key={index} sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={150} height={20} />
                    <Skeleton variant="text" width={100} height={20} />
                  </Box>
                  <Skeleton variant="text" width={80} height={16} />
                </Box>
                <Skeleton variant="text" width="90%" height={16} sx={{ ml: 7 }} />
                <Skeleton variant="text" width="70%" height={16} sx={{ ml: 7 }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error || !boutique) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography variant="h6" color="error">
            {error || 'Boutique non trouvée'}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Navigation Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/" 
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{ cursor: 'pointer' }}
        >
          Accueil
        </Link>
        <Link 
          color="inherit" 
          href="/boutiques" 
          onClick={(e) => { e.preventDefault(); navigate('/boutiques'); }}
          sx={{ cursor: 'pointer' }}
        >
          Boutiques
        </Link>
        <Typography color="text.primary">{boutique.name}</Typography>
      </Breadcrumbs>

      {/* Navigation Boutiques */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/boutiques')}
          variant="outlined"
        >
          Retour
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {getCurrentBoutiqueIndex() + 1} / {boutiques.length}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<ArrowBack />}
              variant="outlined"
              size="small"
              onClick={handlePreviousBoutique}
              disabled={!getPreviousBoutique()}
              title={getPreviousBoutique() ? `Préc: ${getPreviousBoutique().name}` : 'Première boutique'}
            >
              Préc
            </Button>
            <Button
              endIcon={<ArrowForward />}
              variant="outlined"
              size="small"
              onClick={handleNextBoutique}
              disabled={!getNextBoutique()}
              title={getNextBoutique() ? `Suiv: ${getNextBoutique().name}` : 'Dernière boutique'}
            >
              Suiv
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* 📋 Informations boutique - 35% sur desktop, 100% sur mobile */}
        <Grid item xs={12} md={4.2} sx={{ 
          maxWidth: { md: '400px' },
          width: '100%',
        

        }}>
          <Card sx={{ 
            height: '100%', 
            maxHeight: { md: '600px' },
            width: '100%',
            border: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',
            p: 2,
          }}>
            <CardContent sx={{ 
              p: 3, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Logo et nom */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',
                borderRadius: 1,
                p:1,
               }}>
                <Avatar 
                  src={boutique.logo} 
                  sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}
                >
                  <Store sx={{ fontSize: 30 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: 'white' }}>
                    {boutique.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Rating 
                      value={boutique.averageRating || 0} 
                      precision={0.1} 
                      size="small" 
                      readOnly 
                    />
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      ({boutique.totalReviews || 0} avis)
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              {/* Description */}
              <Box sx={{ 
                mb: 2, 
                flex: 1, 
                overflow: 'hidden', 
                display: 'flex', 
                flexDirection: 'column' 
              }}>
                <Typography variant="h6" gutterBottom>
                  À propos
                </Typography>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                    '&:hover': { background: '#a8a8a8' },
                  },
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                    {boutique.description || 'Aucune description disponible'}
                  </Typography>
                </Box>
              </Box>

              {/* Spécialités */}
              {boutique.specialties && boutique.specialties.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Spécialités
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {boutique.specialties.map((specialty, index) => (
                      <Chip 
                        key={index} 
                        label={specialty} 
                        color="primary" 
                        variant="outlined"
                        sx={{ 
                          width: 'fit-content',
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Informations de contact */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
                <List dense>
                  {boutique.address && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LocationOn color="action" sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={boutique.address} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  {boutique.phone && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Phone color="action" sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={boutique.phone} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  {boutique.email && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Email color="action" sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={boutique.email} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  {boutique.openingHours && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Schedule color="action" sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={boutique.openingHours} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>

              {/* Actions - Section fixe en bas */}
              <Box sx={{ flexShrink: 0 }}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    // startIcon={<ShoppingCart />}
                    onClick={() => navigate('/products')}
                    sx={{ flexGrow: 1 ,background: 'linear-gradient(90deg, #9b5de5 0%, #f15bb5 100%)',p:1,}}
                  >
                    * les produits
                  </Button>
                  
                  {(() => {
                    // Si la boutique existe, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
                    const contactPhone = boutique 
                      ? (boutique.whatsapp || boutique.phone)
                      : boutique?.user?.phone;
                    return contactPhone && (
                    <Button
                      variant="contained"
                      startIcon={<WhatsApp />}
                      onClick={handleWhatsAppClick}
                      sx={{ 
                        bgcolor: '#25D366',
                        '&:hover': { bgcolor: '#128C7E' }
                      }}
                    >
                      WhatsApp
                    </Button>
                    );
                  })()}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 🛍️ Produits de la boutique - 65% sur desktop, 100% sur mobile */}
        <Grid item xs={12} md={7.8} sx={{ 
          maxWidth: { md: '725px' },
          width: '100%'
        }}>
          <Card sx={{ 
            position: 'relative',
            width: '100%',
            height: { xs: 'auto', md: '100%' },
            maxHeight: { md: '600px' }
          }}>
            <CardContent sx={{ 
              p: 2, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                Produits de {boutique.name}
              </Typography> */}
              
              {/* Zone scrollable des produits */}
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '3px',
                  '&:hover': { background: '#a8a8a8' },
                },
              }}>
                {productsLoading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1.5,
                    justifyContent: { xs: 'space-between', sm: 'flex-start' }
                  }}>
                    {[...Array(6)].map((_, index) => (
                      <Card key={index} sx={{ 
                        width: { xs: 'calc((100% - 12px) / 2)', sm: 160 },
                        height: 190
                      }}>
                        <Skeleton variant="rectangular" width="100%" height={100} />
                        <CardContent sx={{ p: 1 }}>
                          <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.25 }} />
                          <Skeleton variant="text" width="50%" height={14} />
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : products.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Store sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Aucun produit disponible
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cette boutique n'a pas encore de produits en vente
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1.5,
                    justifyContent: { xs: 'space-between', sm: 'flex-start' }
                  }}>
                    {products.map((product) => (
                      <Card 
                        key={product.id}
                        sx={{ 
                          width: { 
                            xs: 'calc((100% - 12px) / 2)', // 2 cartes par ligne sur mobile (100% - gap) / 2
                            sm: 160 // Largeur fixe sur desktop
                          },
                          height: 190,
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3
                          }
                        }}
                        onClick={() => handleProductClick(product.id)}
                      >
                        <Box sx={{ position: 'relative', width: '100%', flexShrink: 0 }}>
                          {(() => {
                            const productImages = Array.isArray(product.images) 
                              ? product.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
                              : [];
                            const currentIndex = productImageIndices[product.id] || 0;
                            const hasMultipleImages = productImages.length > 1;
                            const currentImage = productImages.length > 0 
                              ? (productImages[currentIndex] || productImages[0])
                              : '/img/no_media.jpg';
                            
                            return (
                              <>
                                <ImageCard
                                  src={currentImage}
                                  alt={product.name}
                                  height={100}
                                  sx={{ borderRadius: 0 }}
                                />
                                
                                {/* Navigation entre images */}
                                {hasMultipleImages && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductImagePrev(e, product.id, productImages.length);
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        left: 4,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        '&:hover': { bgcolor: 'white' },
                                        zIndex: 2,
                                        width: 20,
                                        height: 20
                                      }}
                                    >
                                      <ChevronLeft fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductImageNext(e, product.id, productImages.length);
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        right: 4,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        '&:hover': { bgcolor: 'white' },
                                        zIndex: 2,
                                        width: 20,
                                        height: 20
                                      }}
                                    >
                                      <ChevronRight fontSize="small" />
                                    </IconButton>
                                    {/* Indicateur du nombre d'images */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                        fontSize: '0.6rem',
                                        fontWeight: 500,
                                        zIndex: 2
                                      }}
                                    >
                                      {currentIndex + 1}/{productImages.length}
                                    </Box>
                                  </>
                                )}
                              </>
                            );
                          })()}
                          
                          {(!product.isAvailable || product.stockQuantity === 0) && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                bgcolor: 'error.main',
                                color: 'white',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                zIndex: 3
                              }}
                            >
                              Rupture
                            </Box>
                          )}
                        </Box>
                        
                        <CardContent sx={{ 
                          p: 1, 
                          flexGrow: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.7rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 0.5
                            }}
                          >
                            {product.name}
                          </Typography>
                          
                          <Box>
                            <Typography 
                              variant="caption" 
                              color="primary.main" 
                              sx={{ 
                                fontWeight: 700, 
                                fontSize: '0.75rem',
                                display: 'block',
                                mb: 0.25
                              }}
                            >
                              {parseFloat(product.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '0.65rem'
                              }}
                            >
                              Stock: {product.stockQuantity || 0}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Avis */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Avis ({boutique?.totalReviews || 0})
            </Typography>
            {isAuthenticated && (
              <Button
                variant="contained"
                onClick={() => setReviewFormOpen(true)}
                disabled={hasUserReviewed}
                title={hasUserReviewed ? 'Vous avez déjà laissé un avis' : 'Laisser un avis'}
              >
                Laisser un avis
              </Button>
            )}
          </Box>

          {reviewsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucun avis pour le moment
            </Typography>
          ) : (
            <Stack spacing={3}>
              {reviews.map((review) => (
                <Box key={review.id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar src={review.reviewer?.profilePhoto}>
                      {review.reviewer?.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {review.reviewer?.name || 'Utilisateur anonyme'}
                      </Typography>
                      <Rating value={review.rating} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                      </Typography>
                      {isAuthenticated && user && review.reviewer?.id === user.id && (
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditReview(review)}
                            color="primary"
                            title="Modifier le commentaire"
                            sx={{ fontSize: '12px' }}
                          >
                            Editer
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReview(review.id)}
                            color="error"
                            title="Supprimer le commentaire"
                            sx={{ fontSize: '12px' }}
                          >
                            Supprimer
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {review.comment && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 7 }}>
                      {review.isActive === false ? 'En attente d\'approbation...' : review.comment}
                    </Typography>
                  )}
                  {!review.comment && review.isActive === false && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 7, fontStyle: 'italic' }}>
                      En attente d'approbation...
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour laisser un avis */}
      <Dialog open={reviewFormOpen} onClose={() => setReviewFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Laisser un avis</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Note
            </Typography>
            <Rating
              value={reviewRating}
              onChange={(event, newValue) => {
                setReviewRating(newValue);
              }}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaire (optionnel)"
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReviewFormOpen(false);
            setReviewRating(0);
            setReviewComment('');
          }}>
            Annuler
          </Button>
          <Button onClick={handleSubmitReview} variant="contained" disabled={reviewRating < 1}>
            Publier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modifier un avis */}
      <Dialog open={editReviewOpen} onClose={() => setEditReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier votre commentaire</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Note
            </Typography>
            <Rating
              value={editReviewRating}
              onChange={(event, newValue) => {
                setEditReviewRating(newValue);
              }}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaire (optionnel)"
            value={editReviewComment}
            onChange={(e) => setEditReviewComment(e.target.value)}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditReviewOpen(false);
            setEditingReview(null);
            setEditReviewRating(0);
            setEditReviewComment('');
          }}>
            Annuler
          </Button>
          <Button onClick={handleUpdateReview} variant="contained" disabled={editReviewRating < 1}>
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default BoutiqueDetail;
